import { shouldNotifyApiError } from '@helpers/shouldNotifyApiError';
import { useModalContext } from '@providers/ModalProvider';
import { useAPI } from '@providers/useAPI';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminService, AdminUsersResponseDto, AdminUsersResponseUserDto } from 'app/API';
import { useClampListPage } from 'app/hooks/useClampListPage';
import { useDebouncedValue } from 'app/hooks/useDebouncedValue';
import { useListQueryParams } from 'app/hooks/useListQueryParams';
import { adminUsersKeys, TAdminUsersListParams } from 'app/queries/adminUsers/adminUsersKeys';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { EErrorMessages } from 'app/types/errorMessages';
import { createContext, ReactNode, useContext, useEffect, useMemo, useRef } from 'react';
import toast from 'react-hot-toast';
import { fetchAllUsersForExport } from '../api/fetchAllUsersForExport';
import { createFetchUsersQueryFn } from '../api/fetchUsers';
import { downloadUsersCsv } from '../helpers/downloadUsersCsv';

const SEARCH_DEBOUNCE_MS = 300;
const EMPTY_USERS: AdminUsersResponseDto = { data: [], count: 0 };

type TState = {
  users: AdminUsersResponseDto;
  isLoading: boolean;
  hasInitialError: boolean;
  retryUsers: () => void;
  sortingField: string;
  sortingReverse: boolean;
  toggleSort: (field: string) => void;
  search: string;
  setSearch: (v: string) => void;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (v: number) => void;
  reloadPage: () => void;
  createUser: () => void;
  changeEnabled: (user: AdminUsersResponseUserDto) => void;
  verifyEmail: (user: AdminUsersResponseUserDto) => void;
  confirmUser: (user: AdminUsersResponseUserDto) => void;
  exportCSV: () => void;
};

const PageContext = createContext<TState>({
  users: EMPTY_USERS,
  isLoading: false,
  hasInitialError: false,
  retryUsers: () => null,
  sortingField: 'createdAt',
  sortingReverse: true,
  toggleSort: () => null,
  search: '',
  setSearch: () => null,
  page: 1,
  pageSize: 25,
  setPage: () => null,
  setPageSize: () => null,
  reloadPage: () => null,
  createUser: () => null,
  changeEnabled: () => null,
  verifyEmail: () => null,
  confirmUser: () => null,
  exportCSV: () => null,
});

type TProps = {
  children: ReactNode;
};

export const PageProvider = ({ children }: TProps) => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const { openModal, closeModal } = useModalContext();
  const { page, pageSize, search, orderBy, orderType, setSearch, setPage, setPageSize, toggleSort } =
    useListQueryParams({
      defaults: { pageSize: 25, orderBy: 'createdAt', orderType: 'desc' },
    });
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const isSearchSettling = search !== debouncedSearch;
  const lastNotifiedErrorUpdatedAt = useRef(0);
  const listParams = useMemo<TAdminUsersListParams>(
    () => ({ page, pageSize, orderBy, orderType, search: debouncedSearch }),
    [debouncedSearch, orderBy, orderType, page, pageSize],
  );

  const usersQuery = useQuery({
    queryKey: adminUsersKeys.list(listParams),
    queryFn: createFetchUsersQueryFn(call),
    placeholderData: keepPreviousData,
    enabled: !isSearchSettling,
  });

  useClampListPage({
    page,
    setPage,
    pageSize,
    count: usersQuery.data?.count,
    hasFreshData: usersQuery.isSuccess && !usersQuery.isPlaceholderData,
  });

  useEffect(() => {
    if (
      !usersQuery.isRefetchError ||
      usersQuery.data === undefined ||
      usersQuery.errorUpdatedAt === lastNotifiedErrorUpdatedAt.current
    ) {
      return;
    }
    lastNotifiedErrorUpdatedAt.current = usersQuery.errorUpdatedAt;
    if (shouldNotifyApiError(usersQuery.error)) {
      toast.error('Unable to refresh users. Showing previously loaded data.');
    }
  }, [usersQuery.data, usersQuery.error, usersQuery.errorUpdatedAt, usersQuery.isRefetchError]);

  const invalidateUsers = () => queryClient.invalidateQueries({ queryKey: adminUsersKeys.lists() });

  const setDisabledMutation = useMutation({
    mutationFn: ({ user, disabled }: { user: AdminUsersResponseUserDto; disabled: boolean }) =>
      call(() => AdminService.setUserDisabled({ uid: user.id, requestBody: { disabled } }), {
        onError: () => toast.error(EErrorMessages.DEFAULT),
      }),
    onSettled: invalidateUsers,
  });

  const verifyEmailMutation = useMutation({
    mutationFn: (user: AdminUsersResponseUserDto) =>
      call(() => AdminService.verifyUserEmail({ userId: user.id }), {
        onError: () => toast.error(EErrorMessages.DEFAULT),
      }),
    onSuccess: async (_, user) => {
      await invalidateUsers();
      toast.success(`${user.email} has successfully verified`);
    },
  });

  const confirmUserMutation = useMutation({
    mutationFn: (user: AdminUsersResponseUserDto) =>
      call(() => AdminService.confirmUser({ userId: user.id }), {
        onError: () => toast.error(EErrorMessages.DEFAULT),
      }),
    onSuccess: async (_, user) => {
      await invalidateUsers();
      toast.success(`User account ${user.email} has successfully confirmed`);
    },
  });

  const exportMutation = useMutation({
    mutationFn: () => fetchAllUsersForExport(call, debouncedSearch),
    onSuccess: ({ data }) => {
      void downloadUsersCsv(data);
    },
    onError: () => toast.error('Unable to export users.'),
  });

  const reloadPage = () => {
    void invalidateUsers();
  };

  const retryUsers = () => {
    void usersQuery.refetch();
  };

  const createUser = () => {
    const closeAndReload = () => {
      closeModal();
      reloadPage();
    };
    openModal(ModalNamesEnum.CreateUserModal, { onSuccess: closeAndReload, onCancel: closeAndReload });
  };

  const changeEnabled = (user: AdminUsersResponseUserDto) => {
    setDisabledMutation.mutate({ user, disabled: !user.disabled });
  };

  const exportCSV = () => {
    if (!exportMutation.isPending && !isSearchSettling) {
      exportMutation.mutate();
    }
  };

  const isLoading =
    usersQuery.isFetching ||
    isSearchSettling ||
    setDisabledMutation.isPending ||
    verifyEmailMutation.isPending ||
    confirmUserMutation.isPending ||
    exportMutation.isPending;
  const hasInitialError = usersQuery.isError && usersQuery.data === undefined;

  return (
    <PageContext.Provider
      value={{
        users: usersQuery.data ?? EMPTY_USERS,
        isLoading,
        hasInitialError,
        retryUsers,
        sortingField: orderBy,
        sortingReverse: orderType === 'desc',
        toggleSort,
        search,
        setSearch,
        page,
        setPage,
        pageSize,
        setPageSize,
        reloadPage,
        createUser,
        changeEnabled,
        verifyEmail: verifyEmailMutation.mutate,
        confirmUser: confirmUserMutation.mutate,
        exportCSV,
      }}
    >
      {children}
    </PageContext.Provider>
  );
};

export const usePageContext = () => useContext(PageContext);
